import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  installOfflineInterceptor,
  setQueueChangeHandler,
  getPendingCount,
  flushQueue,
} from "@/lib/offlineQueue";
import { useToast } from "@/components/SimpleToast";

const OfflineContext = createContext(null);

export const OfflineProvider = ({ children }) => {
  const toast = useToast();
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const flushingRef = useRef(false);

  const refreshCount = useCallback(async () => {
    setPendingCount(await getPendingCount());
  }, []);

  const doFlush = useCallback(async () => {
    if (flushingRef.current || !navigator.onLine) return;
    if ((await getPendingCount()) === 0) return;
    flushingRef.current = true;
    setSyncing(true);
    try {
      const { synced, failed } = await flushQueue();
      if (synced > 0) {
        toast.success(`${synced} ${synced === 1 ? "Eintrag" : "Einträge"} synchronisiert`);
        window.dispatchEvent(new CustomEvent("offline-sync-complete", { detail: { synced } }));
      }
      if (failed > 0) {
        toast.error(`${failed} ${failed === 1 ? "Eintrag" : "Einträge"} konnten nicht synchronisiert werden`);
      }
    } finally {
      flushingRef.current = false;
      setSyncing(false);
      await refreshCount();
    }
  }, [toast, refreshCount]);

  useEffect(() => {
    installOfflineInterceptor();
    setQueueChangeHandler(refreshCount);
    refreshCount();

    const onOnline = () => {
      setIsOnline(true);
      doFlush();
    };
    const onOffline = () => setIsOnline(false);
    const onQueued = () => refreshCount();

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queued", onQueued);

    // Try to sync anything left over from a previous session.
    if (navigator.onLine) doFlush();

    // Safety net in case an "online" event was missed.
    const interval = setInterval(() => {
      if (navigator.onLine) doFlush();
    }, 30000);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queued", onQueued);
      clearInterval(interval);
    };
  }, [doFlush, refreshCount]);

  return (
    <OfflineContext.Provider value={{ isOnline, pendingCount, syncing, flushNow: doFlush }}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () =>
  useContext(OfflineContext) || { isOnline: true, pendingCount: 0, syncing: false, flushNow: () => {} };
